import { useEffect } from 'react';
import { MdWarningAmber } from 'react-icons/md';

/**
 * Small confirmation popup, shared by the dashboard card and the Announcements page.
 * Closes on the backdrop, on Escape and on Cancel; only the confirm button acts.
 */
const ConfirmDialog = ({
  title = 'Are you sure?',
  message,
  confirmLabel = 'Delete',
  busy = false,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  return (
    <div
      onClick={onCancel}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
    >
      {/* Clicks inside the panel must not bubble up to the closing overlay. */}
      <div
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm rounded-[20px] bg-white p-6 shadow-3xl dark:!bg-navy-800 dark:text-white"
      >
        <div className="mb-3 flex items-center gap-3">
          <span className="rounded-full bg-red-100 p-2 text-red-500 dark:bg-navy-700">
            <MdWarningAmber className="h-6 w-6" />
          </span>
          <h4 className="text-lg font-semibold text-navy-700 dark:text-white">
            {title}
          </h4>
        </div>

        {message && (
          <p className="mb-5 text-sm text-gray-600 dark:text-gray-300">
            {message}
          </p>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-navy-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-600 disabled:opacity-60"
          >
            {busy ? 'Deleting...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
